import { encodeFunctionData, isAddress, zeroAddress, type Address } from 'viem';
import { z } from 'zod';
import { experimentalTriggerOrderAbi } from './conditionalOrderReceipt.js';
import { experimentalLinkedAllocationAbi } from './conditionalLinkedAllocation.js';

const positive128 = z.bigint().min(1n).max((1n << 128n) - 1n);
const uint256 = z.bigint().min(0n).max((1n << 256n) - 1n);
const registration = z.object({
  version: z.enum(['conditional-trigger-order.1', 'conditional-linked-allocation.1']),
  chainId: z.literal(4663),
  engine: z.custom<Address>(value => typeof value === 'string'
    && isAddress(value, { strict: false }) && value.toLowerCase() !== zeroAddress),
  buy: z.boolean(),
  maximumInput: positive128,
  minimumTotalOutput: positive128,
  maximumFeeBps: z.number().int().min(0).max(10_000),
  expiry: uint256,
  trigger: z.object({
    threshold: uint256.refine(value => value > 0n),
    atOrBelow: z.boolean(),
    latchAfterFirstFill: z.boolean(),
  }).strict(),
}).strict();

export type ExperimentalConditionalRegistration = z.infer<typeof registration>;

/** Unsigned owner-call encoding only. Every economic/trigger term is explicit.
 * No wallet permission, deployed-code approval, live oracle choice, default floor,
 * linked-exit policy, simulation or permission to submit is inferred here.
 * Expiry must also be checked against the execution block by the contract.
 */
export function prepareExperimentalConditionalRegistration(input: ExperimentalConditionalRegistration) {
  const terms = registration.parse(input);
  const data = encodeFunctionData({ abi: experimentalTriggerOrderAbi, functionName: 'registerConditional',
    args: [terms.buy, terms.maximumInput, terms.minimumTotalOutput, terms.maximumFeeBps,
      terms.expiry, terms.trigger] });
  return {
    terms,
    call: { target: terms.engine, value: 0n, data },
    authorizationRequired: 'vault-owner' as const,
    submissionAuthorized: false as const,
  };
}

const linkedRegistration = z.object({
  version: z.literal('conditional-linked-allocation.1'), chainId: z.literal(4663),
  engine: registration.shape.engine, maximumInput: positive128,
  first: registration.pick({ minimumTotalOutput: true, maximumFeeBps: true, expiry: true, trigger: true }),
  second: registration.pick({ minimumTotalOutput: true, maximumFeeBps: true, expiry: true, trigger: true }),
}).strict();
export type ExperimentalLinkedRegistration = z.infer<typeof linkedRegistration>;

/** Two explicitly authorized sell legs sharing one input allocation. No OCO
 * winner policy, automatic defaults, owner authority or submission is inferred.
 * Ordering is preserved because the contract assigns adjacent IDs in this order.
 */
export function prepareExperimentalLinkedRegistration(input: ExperimentalLinkedRegistration) {
  const terms = linkedRegistration.parse(input);
  return { terms, call: { target: terms.engine, value: 0n,
    data: encodeFunctionData({ abi: experimentalLinkedAllocationAbi, functionName: 'registerAllocation',
      args: [terms.maximumInput, terms.first, terms.second] }) },
    authorizationRequired: 'vault-owner' as const, submissionAuthorized: false as const };
}
